import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { BookOpen, Headphones, Sparkles, Moon } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useContentI18n } from "@/context/ContentI18n";
import { usePlayer } from "@/context/PlayerContext";
import { Header } from "@/components/Header";
import { PlayerBar } from "@/components/PlayerBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/0e3d4d61-ed21-4f86-b551-f282d91748f8/images/b10e4dbbe9b7de0d3e99621f2eb8690214eaacf5da90ecb261ab6a34a1d680f5.jpeg";

export default function StoryKamare() {
  const { t, lang, fontClass } = useLang();
  const { title: ctitle, text: ctext } = useContentI18n();
  const { current } = usePlayer();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    axios.get(`${API}/books?category=kids`).then((r) => { if (live) setBooks(r.data); })
      .catch(() => {}).finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);

  return (
    <div className="min-h-screen pb-32" style={{ background: "var(--bg)" }}>
      <Header />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, color-mix(in srgb, var(--bg) 40%, transparent) 0%, color-mix(in srgb, var(--bg) 78%, transparent) 55%, var(--bg) 100%)" }} />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-14 pb-12 sm:pt-20 sm:pb-16">
          <div className="max-w-2xl sk-fade-up">
            <span className="inline-flex items-center gap-2 font-ui text-xs sm:text-sm font-bold tracking-widest uppercase px-3 py-1.5 rounded-full mb-5" style={{ background: "var(--brass-light)", color: "var(--accent)" }}>
              <Sparkles size={16} /> {t("st_nav")}
            </span>
            <h1 className={`${fontClass()} text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight`} style={{ color: "var(--text)" }}>
              {t("st_title")}
            </h1>
            <p className="font-ui text-base sm:text-lg leading-relaxed mt-4" style={{ color: "var(--text-2)" }}>
              {t("st_sub")}
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-8">
        {loading ? (
          <div className="py-16 text-center font-ui text-lg" style={{ color: "var(--text-muted)" }}>{t("loading")}</div>
        ) : books.length === 0 ? (
          <div className="py-16 text-center font-ui text-lg" style={{ color: "var(--text-muted)" }}>{t("st_empty")}</div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {books.map((b) => (
              <Link
                key={b.id}
                to={`/rooms/story-kamare/${b.id}`}
                data-testid={`story-card-${b.id}`}
                className="text-left rounded-3xl p-4 transition-transform hover:scale-[1.02] active:scale-95"
                style={{ background: "var(--card)", border: "2px solid var(--border)" }}
              >
                <div className="relative">
                  <img src={b.cover_url} alt={b.title_en} className="w-full aspect-square rounded-2xl object-cover" />
                  <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 font-ui text-xs font-bold px-2.5 py-1.5 rounded-full" style={{ background: "var(--player-bg)", color: "var(--player-accent)" }}>
                    <Headphones size={13} /> {b.chapter_count}
                  </span>
                </div>
                <p className={`${fontClass()} text-lg sm:text-xl font-bold mt-3 leading-snug line-clamp-2`} style={{ color: "var(--text)" }}>
                  {ctitle(b)}
                </p>
                <p className="font-ui text-sm truncate mt-0.5" style={{ color: "var(--text-muted)" }}>
                  {ctext(b.author)}
                </p>
              </Link>
            ))}
          </div>
        )}
      </section>

      {current && <div style={{ height: 8 }} />}
      <PlayerBar />
    </div>
  );
}
